/**
 * Tâche 1143 (Eric 2026-06-16) — Panneau « Notifications système ».
 *
 * Huit familles de courriels automatiques n'obéissaient à AUCUN réglage : elles
 * interrogeaient la base directement et écrivaient à tous les fondateurs et
 * administrateurs — c'est pour ça que Jean-Sébastien recevait encore
 * « Redémarrage après incident » pour FLHQ alors qu'il était décoché partout.
 *
 * Une liste de destinataires PAR FAMILLE (choix d'Eric) : recevoir les alertes
 * de budget n'oblige plus à recevoir les redémarrages.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellSimpleRinging } from "@phosphor-icons/react";
import { confirmDialog } from "../../lib/uiDialogs";
import AlertDeliveryModeCard from "./AlertDeliveryModeCard";
// Tâche 1150 — Un bouton pour tester les huit familles d'un coup.
import TestAllFamiliesButton from "./TestAllFamiliesButton";
import SystemNotificationFamily from "./SystemNotificationFamily";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/system-notifications/settings`;

export const SystemNotificationsCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(BASE, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.families) setData(res.data);
      else await load();
      if (okMessage) toast.success(okMessage);
      return res?.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opération impossible");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = (family, patch) =>
    run(
      () => axios.patch(`${BASE}/${family}`, patch, { withCredentials: true }),
      "Réglages enregistrés"
    );

  const verify = async (family, email) => {
    if (!email) return;
    const res = await run(() =>
      axios.post(`${BASE}/${family}/verify`, { email }, { withCredentials: true })
    );
    if (!res) return;
    setData(res.settings || null);
    if (res.sent) toast.success(`Courriel de vérification envoyé à ${res.email}`);
    else
      toast.info(
        `${res.email} : aucun courriel envoyé (${res.reason}). ` +
          (res.verified ? "Adresse marquée vérifiée." : "")
      );
  };

  const remove = async (family, email) => {
    const ok = await confirmDialog(
      `Retirer définitivement ${email} de cette famille de notifications ?`
    );
    if (!ok) return;
    run(
      () =>
        axios.delete(`${BASE}/${family}/recipients/${encodeURIComponent(email)}`, {
          withCredentials: true,
        }),
      `${email} retiré`
    );
  };

  const sendNow = async (family) => {
    const res = await run(() =>
      axios.post(`${BASE}/${family}/send-now`, {}, { withCredentials: true })
    );
    if (!res) return;
    if (res.sent) toast.success(`Envoyé à ${res.recipients.join(", ")}`);
    else toast.info(`Rien envoyé : ${res.reason}`);
  };

  if (!data) return null;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="system-notifications-settings"
    >
      <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
        <BellSimpleRinging size={18} />
        Notifications système — qui reçoit quoi
      </h2>
      <p className="text-xs text-zinc-500 mt-0.5 mb-3 max-w-3xl">
        Ces courriels automatiques n'obéissaient à aucun réglage : ils écrivaient à
        tous les fondateurs et administrateurs, sans interrupteur. Chaque famille a
        maintenant sa propre liste, propre à <strong>cette organisation</strong>. Tout
        démarre <strong>en pause</strong> : rien ne part avant que vous ayez coché une
        adresse, l'ayez vérifiée avec « Tester », puis activé l'envoi.
      </p>
      {!data.emails_allowed_here && (
        <p
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="sysnotif-preview-notice"
        >
          Environnement de préproduction : <strong>aucune alerte n'est envoyée
          d'ici</strong>, quels que soient les réglages. Les adresses testées sont
          quand même marquées vérifiées pour que la configuration reste possible.
        </p>
      )}

      <div className="mb-4">
        <TestAllFamiliesButton />
      </div>

      <div className="space-y-4">
        {/* Tâche 1144 — Cadence de remise : critique immédiat, reste en résumé. */}
        <AlertDeliveryModeCard />
        {(data.families || []).map((family) => (
          <SystemNotificationFamily
            key={family.key}
            family={family}
            busy={busy}
            onSave={save}
            onVerify={verify}
            onRemove={remove}
            onSendNow={sendNow}
          />
        ))}
      </div>
    </section>
  );
};

export default SystemNotificationsCard;
